// src/pages/SignIn.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import '../Navigation.css';

// Authentication button logos
const googleLogo = 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/google/google-original.svg';
const facebookLogo = 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/facebook/facebook-original.svg';

function SignIn() {
  const { currentUser, signInWithGoogle, signInWithFacebook, login, signup, resetPassword } = useAuth();
  const navigate = useNavigate();
  
  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // If user is already signed in, redirect to home
  useEffect(() => {
    if (currentUser) {
      navigate('/');
    }
  }, [currentUser, navigate]);
  
  // Handle Google sign in
  const handleGoogleSignIn = async () => {
    try {
      setError('');
      await signInWithGoogle();
      // Navigation will happen automatically due to the useEffect
    } catch (error) {
      console.error("Error during sign in:", error);
      setError("Failed to sign in with Google. Please try again.");
    }
  };
  
  // Handle Facebook sign in
  const handleFacebookSignIn = async () => {
    try {
      setError('');
      await signInWithFacebook();
      // Navigation will happen automatically due to the useEffect
    } catch (error) {
      console.error("Error during Facebook sign in:", error);
      
      // Special error handling for common Facebook auth issues
      if (error.code === 'auth/account-exists-with-different-credential') {
        setError("An account already exists with the same email address but different sign-in credentials. Sign in using the provider associated with this email address.");
      } else {
        setError("Failed to sign in with Facebook. Please try again.");
      }
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous messages
    setError('');
    setMessage('');
    setIsSubmitting(true);
    
    try {
      if (isResettingPassword) {
        // Handle password reset
        await resetPassword(email);
        setMessage("Check your inbox for password reset instructions.");
      } else if (isRegistering) {
        // Validate terms acceptance for registration
        if (!acceptTerms) {
          setError("You must accept the Terms and Conditions to create an account.");
          setIsSubmitting(false);
          return;
        }
        // Handle sign up
        await signup(email, password, displayName);
        // Navigation will happen automatically due to the useEffect
      } else {
        // Handle login
        await login(email, password);
        // Navigation will happen automatically due to the useEffect
      }
    } catch (error) {
      let errorMessage = "Failed to ";
      
      if (isResettingPassword) errorMessage += "reset password.";
      else if (isRegistering) errorMessage += "sign up.";
      else errorMessage += "sign in.";
      
      if (error.code === 'auth/user-not-found') {
        errorMessage = "No account found with this email.";
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = "Incorrect password.";
      } else if (error.code === 'auth/weak-password') {
        errorMessage = "Password should be at least 6 characters.";
      } else if (error.code === 'auth/email-already-in-use') {
        errorMessage = "An account with this email already exists.";
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = "Please enter a valid email address.";
      }
      
      setError(errorMessage);
    }
    
    setIsSubmitting(false);
  };
  
  // Switch between login, register, and password reset modes
  const toggleMode = () => {
    setError('');
    setMessage('');
    setAcceptTerms(false);
    setIsRegistering(!isRegistering);
    setIsResettingPassword(false);
  };
  
  const togglePasswordReset = () => {
    setError('');
    setMessage('');
    setAcceptTerms(false);
    setIsResettingPassword(!isResettingPassword);
    setIsRegistering(false);
  };
  
  return (
    <div className="signin-container">
      <h1>{isResettingPassword ? "Reset Password" : (isRegistering ? "Create Account" : "Sign In")}</h1>
      
      {!isResettingPassword && (
        <p>
          {isRegistering 
            ? "Create an account to access all features including adding listings and managing your account." 
            : "Sign in to access all features including adding listings and managing your account."}
        </p>
      )}
      
      {isResettingPassword && (
        <p>Enter your email address and we'll send you a link to reset your password.</p>
      )}
      
      {error && <div style={{ color: '#e74c3c', marginBottom: '1rem', backgroundColor: '#fceae8', padding: '0.75rem', borderRadius: '4px' }}>{error}</div>}
      {message && <div style={{ color: '#27ae60', marginBottom: '1rem', backgroundColor: '#e8f8f0', padding: '0.75rem', borderRadius: '4px' }}>{message}</div>}
      
      <form onSubmit={handleSubmit} style={{ marginBottom: '2rem' }}>
        {!isResettingPassword && isRegistering && (
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="displayName" style={{ display: 'block', marginBottom: '0.5rem', textAlign: 'left' }}>Display Name</label>
            <input
              type="text"
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required={isRegistering}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>
        )}
        
        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="email" style={{ display: 'block', marginBottom: '0.5rem', textAlign: 'left' }}>Email</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
          />
        </div>
        
        {!isResettingPassword && (
          <div style={{ marginBottom: '1rem' }}>
            <label htmlFor="password" style={{ display: 'block', marginBottom: '0.5rem', textAlign: 'left' }}>Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required={!isResettingPassword}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          </div>
        )}
        
        {!isResettingPassword && isRegistering && (
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', textAlign: 'left', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                style={{ marginRight: '0.5rem', marginTop: '0.2rem' }}
                required={isRegistering}
              />
              <span style={{ fontSize: '0.9rem', lineHeight: '1.4' }}>
                I accept the{' '}
                <a 
                  href="https://docs.google.com/document/d/1KW3e5nP5n3xAFi5iBMgykfadaB_1TrSf/edit?usp=drive_link&ouid=105347652403814580551&rtpof=true&sd=true" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}
                >
                  Terms and Conditions
                </a>
                {' '}and{' '}
                <a 
                  href="https://docs.google.com/document/d/1cvex61ZBqv98UACTi7cCDE3GjuY_nILi/edit?usp=drive_link&ouid=105347652403814580551&rtpof=true&sd=true" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}
                >
                  Privacy Policy
                </a>
              </span>
            </label>
          </div>
        )}
        
        <button 
          type="submit" 
          disabled={isSubmitting}
          style={{ 
            backgroundColor: 'var(--color-primary)', 
            color: 'white', 
            border: 'none', 
            padding: '0.75rem 1.5rem', 
            width: '100%',
            borderRadius: '4px', 
            marginTop: '1rem',
            cursor: isSubmitting ? 'not-allowed' : 'pointer'
          }}
        >
          {isSubmitting ? 'Processing...' : (
            isResettingPassword ? 'Send Reset Link' : (
              isRegistering ? 'Create Account' : 'Sign In'
            )
          )}
        </button>
      </form>
      
      {!isResettingPassword && (
        <div style={{ marginBottom: '1.5rem' }}>
          <p>
            {isRegistering ? 'Already have an account?' : "Don't have an account?"}
            <button 
              onClick={toggleMode} 
              style={{ 
                background: 'none', 
                border: 'none', 
                color: 'var(--color-primary)', 
                cursor: 'pointer',
                textDecoration: 'underline',
                marginLeft: '0.5rem'
              }}
            >
              {isRegistering ? 'Sign In' : 'Create Account'}
            </button>
          </p>
        </div>
      )}
      
      {!isRegistering && !isResettingPassword && (
        <div style={{ marginBottom: '1.5rem' }}>
          <button 
            onClick={togglePasswordReset} 
            style={{ 
              background: 'none', 
              border: 'none', 
              color: 'var(--color-primary)', 
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            Forgot Password?
          </button>
        </div>
      )}
      
      {isResettingPassword && (
        <div>
          <button 
            onClick={() => {
              setIsResettingPassword(false);
              setError('');
              setMessage('');
            }} 
            style={{ 
              background: 'none', 
              border: 'none', 
              color: 'var(--color-primary)', 
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            Back to Sign In
          </button>
        </div>
      )}
      
      {!isResettingPassword && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', margin: '1.5rem 0' }}>
            <div style={{ flex: 1, height: '1px', backgroundColor: '#ddd' }}></div>
            <p style={{ margin: '0 10px', color: '#666' }}>OR</p>
            <div style={{ flex: 1, height: '1px', backgroundColor: '#ddd' }}></div>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <button onClick={handleGoogleSignIn} className="google-signin-btn">
              <img src={googleLogo} alt="Google" className="google-icon" />
              {isRegistering ? 'Sign up with Google' : 'Sign in with Google'}
            </button>
            
            <button onClick={handleFacebookSignIn} className="facebook-signin-btn">
              <img src={facebookLogo} alt="Facebook" className="facebook-icon" />
              {isRegistering ? 'Sign up with Facebook (Under Development)' : 'Sign in with Facebook (Under Development)'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default SignIn;
